import CloseIcon from "@mui/icons-material/CloseRounded";
import { Box, Divider, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTemplateShippingProfiles } from "@services/template";
import CountrySelector from "components/countrySelector";
import { useContainer, useTemplate } from "containers/templateDetail/store";
import cloneDeep from "lodash/cloneDeep";
import head from "lodash/head";
import * as React from "react";
import useCountries, { Country } from "stores/useStore/useCountries";

const TableCell = styled("div")<{
  variant?: "head" | "body";
  padding?: "none" | "default";
}>(({ theme, variant = "body", padding = "default" }) => ({
  fontSize: theme.typography.body1.fontSize,
  fontWeight:
    variant === "head"
      ? theme.typography.fontWeightSemiBold
      : theme.typography.fontWeightRegular,
  color: theme.palette.text.primary,
  display: "flex",
  alignItems: "center",
  flex: variant === "body" ? 1 : "none",
  padding: padding === "none" && variant === "body" ? 0 : "8px 0px",
  justifyContent: variant === "body" ? "flex-end" : "flex-start",
  [theme.breakpoints.down("md")]: {
    width: variant === "head" ? 125 : "auto",
  },
  [theme.breakpoints.up("md")]: {
    padding:
      padding === "none"
        ? `${theme.spacing(2)} 0px`
        : `${theme.spacing(2)} ${theme.spacing(3)}`,
    borderBottom: variant === "head" ? "1px solid" : "0px",
    borderBottomColor: theme.palette.divider,
    justifyContent: "flex-start",
  },
}));

const TableColumn = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  [theme.breakpoints.up("md")]: {
    flexDirection: "column",
  },
}));

const Table = ({
  initCountryCode,
  onChange,
  comparedCountries,
  setComparedCountries,
}: {
  initCountryCode: string;
  onChange: (country: Country) => void;
  comparedCountries: any[];
  setComparedCountries: (list: any) => void;
}) => {
  const { id: templateID, grams: templateGrams } = useTemplate();
  const { data: shippingProfiles } = useTemplateShippingProfiles({
    id: templateID,
  });
  const [activeShippingProfileID] = useContainer.activeShippingProfileID();

  const activeShippingProfile = React.useMemo(() => {
    return shippingProfiles?.find(
      (item) => item.id === activeShippingProfileID
    );
  }, [activeShippingProfileID]);

  const { data: countries } = useCountries();

  const defaultCountry = React.useMemo(() => {
    return countries?.find((item) => item.isoNumeric === initCountryCode);
  }, [initCountryCode, countries]);

  const [selectedCountry, setSelectedCountry] = React.useState(defaultCountry);

  React.useEffect(() => {
    if (defaultCountry) {
      setSelectedCountry(defaultCountry);
    }
  }, [defaultCountry]);

  const handleChange = (ev, country) => {
    setSelectedCountry(country);
    onChange(country);
  };

  const comparedCountryValues = React.useMemo(() => {
    const arr = [];
    if (activeShippingProfile?.type === "DROP_SHIPPING") {
      // TODO: 将来可能要改为选择sku
      const shippingRate = head(activeShippingProfile.rates);
      for (let i = 0; i < comparedCountries.length; i++) {
        const comparedCountry = comparedCountries[i];
        const shippingRatePrice = shippingRate.prices.find(
          (item) => item.zone_id === comparedCountry.zone_id
        );
        arr[i] = {
          country: comparedCountry,
          country_code: comparedCountry.isoNumeric,
          delivery_time: `${shippingRate?.min_shipping_time || 0} ⁠– ${
            shippingRate?.max_shipping_time || 0
          }⁠ business days`,
          first_column: `$${shippingRatePrice.first_price}`,
          secondary_column: `$${shippingRatePrice.additional_price}`,
        };
      }
    } else if (activeShippingProfile?.type === "BULK_SHIPPING") {
      const gramPrices = activeShippingProfile.gram_prices;
      for (let i = 0; i < comparedCountries.length; i++) {
        const comparedCountry = comparedCountries[i];
        const gramPrice = gramPrices.find(
          (item) => item.country_id === comparedCountry.id
        );
        if (gramPrice) {
          arr[i] = {
            country: comparedCountry,
            country_code: comparedCountry.isoNumeric,
            delivery_time: `${gramPrice?.min_shipping_time || 0} ⁠– ${
              gramPrice?.max_shipping_time || 0
            }⁠ business days`,
            first_column: Math.ceil(gramPrice.start_gram / templateGrams),
            secondary_column: `$${(
              parseFloat(gramPrice.price_per_gram) * templateGrams
            ).toFixed(2)}`,
          };
        } else {
          arr[i] = {
            country: comparedCountry,
            country_code: comparedCountry.isoNumeric,
            delivery_time: "Country Not Supported",
            first_column: "",
            secondary_column: "",
          };
        }
      }
    }

    return arr;
  }, [comparedCountries, activeShippingProfile]);

  const comparedCountryChanged = (country, index) => {
    const arr = cloneDeep(comparedCountries);
    if (country.isoNumeric === selectedCountry.isoNumeric) {
      arr.splice(index, 1);
    } else if (
      comparedCountries.findIndex((item, index2) => {
        return item.isoNumeric === country.isoNumeric && index2 !== index;
      }) !== -1
    ) {
      arr.splice(index, 1);
    } else {
      arr[index] = country;
    }
    setComparedCountries(arr);
  };

  const onDelete = (index) => {
    const arr = cloneDeep(comparedCountries);
    arr.splice(index, 1);
    setComparedCountries(arr);
  };

  const [
    activeCountryDeliveryTime,
    activeCountryFirstColumn,
    activeCountrySecondaryColumn,
  ] = React.useMemo(() => {
    if (activeShippingProfile?.type === "DROP_SHIPPING") {
      const shippingRate = head(activeShippingProfile.rates);
      const shippingRatePrice = shippingRate.prices.find(
        (item) => item.zone_id === defaultCountry.zone_id
      );
      return [
        `${shippingRate?.min_shipping_time || 0} ⁠– ${
          shippingRate?.max_shipping_time || 0
        }⁠ business days`,
        `$${shippingRatePrice.first_price}`,
        `$${shippingRatePrice.additional_price}`,
      ];
    } else if (activeShippingProfile?.type === "BULK_SHIPPING") {
      const gramPrice = activeShippingProfile.gram_prices.find(
        (item) => item.country_id === defaultCountry.id
      );

      if (!gramPrice) {
        return ["Country Not Supported", "", ""];
      }

      return [
        `${gramPrice?.min_shipping_time || 0} ⁠– ${
          gramPrice?.max_shipping_time || 0
        }⁠ business days`,
        Math.ceil(gramPrice.start_gram / templateGrams),
        `$${(parseFloat(gramPrice.price_per_gram) * templateGrams).toFixed(2)}`,
      ];
    }
  }, [activeShippingProfile, defaultCountry]);

  if (!defaultCountry) {
    return <></>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
      <Box sx={{ flex: 1, mb: { xs: 1, md: 0 } }}>
        <TableCell variant="head" padding="none">
          Country
        </TableCell>
        <TableCell padding="none" sx={{ justifyContent: "flex-start" }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "none", md: 350 },
            }}
          >
            <CountrySelector
              defaultValue={defaultCountry || null}
              onChange={handleChange}
            />
          </Box>
          {comparedCountryValues.length > 0 && (
            <Box sx={{ width: 54, display: { md: "none" } }}></Box>
          )}
        </TableCell>
        {comparedCountryValues.map((item, index) => {
          return (
            <React.Fragment key={index}>
              <Divider sx={{ display: { xs: "none", md: "block" } }} />
              <Box sx={{ my: 2, display: { xs: "block", md: "none" } }} />
              <TableCell padding="none" sx={{ justifyContent: "flex-start" }}>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "none", md: 350 },
                  }}
                >
                  <CountrySelector
                    value={item.country}
                    defaultValue={item.country}
                    onChange={(ev, country) =>
                      comparedCountryChanged(country, index)
                    }
                  />
                </Box>
                <Box sx={{ width: 54, display: { md: "none" } }}>
                  <Box
                    onClick={() => onDelete(index)}
                    sx={{
                      cursor: "pointer",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CloseIcon />
                  </Box>
                </Box>
              </TableCell>
            </React.Fragment>
          );
        })}
      </Box>
      <TableColumn sx={{ display: { md: "none" } }}>
        <TableCell variant="head">Country</TableCell>
        <TableCell>{defaultCountry.country}</TableCell>
        {comparedCountryValues.map((item, index) => {
          return <TableCell key={index}>{item.country.country}</TableCell>;
        })}
      </TableColumn>
      <TableColumn>
        <TableCell variant="head">
          Delivery time&nbsp;<b style={{ color: "red" }}>*</b>
        </TableCell>
        <TableCell>{activeCountryDeliveryTime}</TableCell>
        {comparedCountryValues.map((item, index) => {
          return (
            <React.Fragment key={index}>
              <Divider />
              <TableCell>{item.delivery_time}</TableCell>
            </React.Fragment>
          );
        })}
      </TableColumn>
      <TableColumn>
        <TableCell variant="head">
          {activeShippingProfile?.type === "DROP_SHIPPING" ? "First item" : ""}
          {activeShippingProfile?.type === "BULK_SHIPPING" ? (
            <>
              MOQ&nbsp;<b style={{ color: "green" }}>*</b>
            </>
          ) : (
            ""
          )}
        </TableCell>
        <TableCell>{activeCountryFirstColumn}</TableCell>
        {comparedCountryValues.map((item, index) => {
          return (
            <React.Fragment key={index}>
              <Divider />
              <TableCell>{item.first_column}</TableCell>
            </React.Fragment>
          );
        })}
      </TableColumn>
      <TableColumn>
        <TableCell variant="head">
          {activeShippingProfile?.type === "DROP_SHIPPING"
            ? "Additional item"
            : ""}
          {activeShippingProfile?.type === "BULK_SHIPPING" ? "Unit Price" : ""}
        </TableCell>
        <TableCell>{activeCountrySecondaryColumn}</TableCell>
        {comparedCountryValues.map((item, index) => {
          return (
            <React.Fragment key={index}>
              <Divider />
              <TableCell>{item.secondary_column}</TableCell>
            </React.Fragment>
          );
        })}
      </TableColumn>
      <TableColumn
        sx={{
          display: {
            xs: "none",
            md: "flex",
          },
        }}
      >
        <TableCell variant="head">&nbsp;</TableCell>
        <TableCell padding="none">&nbsp;</TableCell>
        {comparedCountryValues.map((item, index) => {
          return (
            <React.Fragment key={index}>
              <Divider />
              <TableCell padding="none">
                <Box
                  onClick={() => onDelete(index)}
                  sx={{
                    cursor: "pointer",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloseIcon />
                </Box>
              </TableCell>
            </React.Fragment>
          );
        })}
      </TableColumn>
    </Box>
  );
};

export default function PriceCompareTable(): React.ReactElement {
  const [activeCountryCode, setActiveCountryCode] =
    useContainer.activeCountryCode();
  const [comparedCountries, setComparedCountries] = React.useState([]);

  const handleCountryChange = (country: Country) => {
    setActiveCountryCode(country.isoNumeric);
  };

  const handleChange = (ev, countries) => {
    setComparedCountries(
      countries.filter((item) => item.isoNumeric !== activeCountryCode)
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Table
        comparedCountries={comparedCountries}
        setComparedCountries={setComparedCountries}
        initCountryCode={activeCountryCode}
        onChange={handleCountryChange}
      />
      <Typography
        variant="body1"
        sx={{
          fontWeight: (theme) => theme.typography.fontWeightSemiBold,
          mb: 1.5,
          pt: 1.5,
          borderTop: "1px solid",
          borderTopColor: (theme) => theme.palette.divider,
        }}
      >
        Add more countries to compare
      </Typography>
      <Box
        sx={{
          maxWidth: { xs: "none", md: 350 },
        }}
      >
        <CountrySelector
          multiple={true}
          onChange={handleChange}
          defaultValue={[]}
          value={comparedCountries}
        />
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
        <b style={{ color: "red" }}>*</b> Delivery times are the average
        delivery time of the past 15 days and might differ based on the amount
        of orders to proceed
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        <b style={{ color: "green" }}>*</b> MOQ is minimum order quantity
      </Typography>
    </Box>
  );
}
